import { isRateable, type OrderFeedback } from "@/domain/order-feedback/order-feedback"
import { Card, Heading, Icon, InteractiveCard, RatingStars, Text } from "@/components/ui"
import { StarIcon } from "@/icons"
import { cn } from "@/lib/cn"
import { feedbackMessage } from "@/lib/i18n/feedback-messages"
import type { OrderResponse } from "@/types/external"
import { useState } from "react"
import { OrderRatingSheet, StarPicker } from "./OrderRatingSheet"

type OrderRatingPromptProps = {
  order: OrderResponse
  feedback?: OrderFeedback
  onRated: (feedback: OrderFeedback) => void
  variant: "badge" | "card"
}

/**
 * "Avalie seu pedido" on a rateable, unrated order ("badge" in the list, "card" with inline stars on the detail);
 * "Você já avaliou ★N" once rated; nothing otherwise.
 */
export function OrderRatingPrompt({ order, feedback, onRated, variant }: OrderRatingPromptProps) {
  const [open, setOpen] = useState(false)
  const [preset, setPreset] = useState(0)

  if (feedback) {
    if (variant === "badge") return <RatedLine stars={feedback.stars} className="mt-3" />
    return (
      <Card as="section" padding="lg">
        <RatedLine stars={feedback.stars} className="justify-center" />
      </Card>
    )
  }
  if (!isRateable(order)) return null

  const sheet = (
    <OrderRatingSheet
      orderId={order.id}
      displayId={order.displayId}
      open={open}
      initialStars={preset}
      onOpenChange={setOpen}
      onRated={onRated}
    />
  )

  if (variant === "card") {
    return (
      <Card as="section" padding="lg" aria-labelledby={`rate-${order.id}`}>
        <Heading id={`rate-${order.id}`} level={2} size="section" align="center">
          {feedbackMessage("detailTitle")}
        </Heading>
        <StarPicker
          value={preset}
          onChange={(stars) => {
            setPreset(stars)
            setOpen(true)
          }}
        />
        {sheet}
      </Card>
    )
  }

  return (
    <>
      <InteractiveCard
        variant="tinted"
        tone="primary"
        padding="compact"
        onClick={() => setOpen(true)}
        className="mt-3 w-full"
        data-testid="order-rating-nudge"
      >
        <span className="flex items-center justify-between gap-3">
          <span className="flex min-w-0 items-center gap-2">
            <Icon as={StarIcon} size="md" weight="fill" tone="primary" />
            <Text as="span" size="sm" weight="semibold" tone="default" truncate>
              {feedbackMessage("nudge")}
            </Text>
          </span>
          <Text as="span" size="sm" weight="bold" tone="primary" className="shrink-0">
            {feedbackMessage("rateCta")}
          </Text>
        </span>
      </InteractiveCard>
      {sheet}
    </>
  )
}

function RatedLine({ stars, className }: { stars: number; className?: string }) {
  return (
    <Text as="div" size="sm" tone="muted" className={cn("flex items-center gap-1.5", className)}>
      <span>{feedbackMessage("rated")}</span>
      <RatingStars value={stars} size="sm" label={feedbackMessage("starAria", { n: stars })} />
    </Text>
  )
}
